import type { ConnectionState, ServerFrame } from '@liniya/api-client';
import { RealtimeClient } from '@liniya/api-client';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { api, currentAccessToken } from '../api';
import { queryKeys } from '../query-keys';
import { useUiStore } from '../store/ui';
import { applyRealtimeFrame } from './handlers';

interface RealtimeContextValue {
  state: ConnectionState;
  client: RealtimeClient | null;
  /** Register a listener for frames the shared handlers do not consume, such as call signalling. */
  subscribe: (listener: (frame: ServerFrame) => void) => () => void;
}

const RealtimeContext = createContext<RealtimeContextValue>({
  state: 'idle',
  client: null,
  subscribe: () => () => undefined,
});

/**
 * Where the client is told what changed, and when it stopped being told.
 *
 * Everything below is the same on both clients except two things a browser and
 * a phone do not share: where the stream cursor is kept, and what counts as
 * "the platform just handed control back". Both are supplied from outside, so
 * the handling of the stream itself cannot drift between them.
 */
export interface RealtimeCursorStorage {
  read(): number;
  write(sequence: number): void;
}

/**
 * Register whatever the platform offers as a cue that the connection should be
 * checked, and return the teardown. A dropped socket does not always produce a
 * close event — a frozen tab and a sleeping phone both simply stop — so these
 * moments are used instead of waiting for a heartbeat that will not fire.
 */
export type WakeSignals = (reconnect: () => void) => () => void;

/** Per-process by default: two tabs sharing one cursor would each skip what the other consumed. */
class MemoryCursorStorage implements RealtimeCursorStorage {
  private sequence = 0;

  read(): number {
    return this.sequence;
  }

  write(sequence: number): void {
    this.sequence = sequence;
  }
}

export function RealtimeProvider({
  viewerUserId,
  url,
  cursorStorage,
  wakeSignals,
  children,
}: {
  viewerUserId: string;
  /**
   * Where the socket connects.
   *
   * Only a browser has a current page to take the host from. A native client
   * has to say so outright, and omitting it leaves the connection stuck on
   * "connecting" for the life of the app — which is what happened.
   */
  url?: string;
  cursorStorage?: RealtimeCursorStorage;
  wakeSignals?: WakeSignals;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<ConnectionState>('idle');
  const [client, setClient] = useState<RealtimeClient | null>(null);
  const clientRef = useRef<RealtimeClient | null>(null);
  const listeners = useRef(new Set<(frame: ServerFrame) => void>());
  // Created once: a new storage on every render would reset the cursor.
  const fallbackStorage = useRef(new MemoryCursorStorage());
  const storage = cursorStorage ?? fallbackStorage.current;

  const markRead = useCallback((chatId: string, messageId: string) => {
    void clientRef.current?.trySend('message.read', { message_id: messageId });
    void chatId;
  }, []);

  /**
   * Fill the gap the socket could not replay.
   *
   * When the durable stream has moved too far ahead, the socket refuses to
   * guess. REST is the source of truth, so the missed span is walked over HTTP
   * and only then is the socket told which sequence the client has reached.
   */
  const resync = useCallback(
    async (fromCursor: number) => {
      let cursor = clientRef.current?.lastSequence ?? 0;
      try {
        for (let page = 0; page < 20; page += 1) {
          const result = await api.sync.since(cursor);
          if (result.resync_required) {
            // The cursor cannot be trusted at all: reload from scratch.
            await queryClient.invalidateQueries();
            clientRef.current?.acceptCursor(result.cursor);
            return;
          }
          for (const event of result.events) {
            applyRealtimeFrame(
              {
                version: 1,
                type: event.type,
                event_id: event.event_id,
                sequence: event.sequence,
                occurred_at: event.occurred_at,
                payload: event.payload,
              } as ServerFrame,
              {
                queryClient,
                viewerUserId,
                activeChatId: () => useUiStore.getState().activeChatId,
                markRead,
              },
            );
          }
          cursor = result.cursor;
          clientRef.current?.acceptCursor(cursor);
          if (!result.has_more) break;
        }
        await queryClient.invalidateQueries({ queryKey: queryKeys.chats });
      } catch {
        useUiStore.getState().pushToast('Не удалось синхронизировать сообщения.', 'error');
        void fromCursor;
      }
    },
    [markRead, queryClient, viewerUserId],
  );

  useEffect(() => {
    const client = new RealtimeClient({
      accessToken: currentAccessToken,
      cursorStorage: storage,
      ...(url ? { url } : {}),
      onState: setState,
      onResyncRequired: (currentSequence) => void resync(currentSequence),
      onEvent: (frame) => {
        applyRealtimeFrame(frame, {
          queryClient,
          viewerUserId,
          activeChatId: () => useUiStore.getState().activeChatId,
          markRead,
        });
        for (const listener of listeners.current) listener(frame);
      },
    });
    clientRef.current = client;
    setClient(client);
    client.start();

    const stopWatchingForWake = wakeSignals?.(() => client.reconnectNow());

    return () => {
      stopWatchingForWake?.();
      client.stop();
      clientRef.current = null;
      setClient(null);
    };
  }, [markRead, queryClient, resync, storage, url, viewerUserId, wakeSignals]);

  const subscribe = useCallback((listener: (frame: ServerFrame) => void) => {
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  }, []);

  const value = useMemo<RealtimeContextValue>(
    () => ({ state, client, subscribe }),
    [client, state, subscribe],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): RealtimeContextValue {
  return useContext(RealtimeContext);
}
